import React from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Pencil, Trash2 } from "lucide-react";
import { computeSalary, fmtINR } from "@/components/app/SalaryStructureFormDialog";

/**
 * Read-only-ish revision history table for one employee.
 * `onEdit(r)` and `onDelete(r)` are only wired when `isSuper` is true.
 */
export default function SalaryRevisionHistoryDialog({ historyUser, onClose, rows, isSuper, onEdit, onDelete }) {
  return (
    <Dialog open={!!historyUser} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="ss-history-dialog">
        <DialogHeader>
          <DialogTitle>Revision History · {historyUser?.name}</DialogTitle>
          <DialogDescription>All salary revisions on file, latest first.</DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200 sticky top-0">
              <tr className="text-left text-zinc-500 uppercase text-[10px] tracking-widest">
                <th className="py-2 px-2">Effective</th>
                <th className="py-2 px-2 text-right">Basic</th>
                <th className="py-2 px-2 text-right">HRA</th>
                <th className="py-2 px-2 text-right">Spl</th>
                <th className="py-2 px-2 text-right">Stat.</th>
                <th className="py-2 px-2 text-right">CTC</th>
                <th className="py-2 px-2 text-right">Note</th>
                {isSuper && <th className="py-2 px-2 text-right" />}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const c = computeSalary(r);
                const stat = (Number(r.esi) || 0) + c.pfEmployer + (Number(r.pt) || 0);
                return (
                  <tr key={r.id} className="border-b border-zinc-100" data-testid={`ss-hist-${r.id}`}>
                    <td className="py-2 px-2 font-mono text-xs">{r.effective_from}</td>
                    <td className="py-2 px-2 text-right font-mono">{fmtINR(r.basic)}</td>
                    <td className="py-2 px-2 text-right font-mono">{fmtINR(r.hra)}</td>
                    <td className="py-2 px-2 text-right font-mono">{fmtINR(r.special_allowance)}</td>
                    <td className="py-2 px-2 text-right font-mono">{fmtINR(stat)}</td>
                    <td className="py-2 px-2 text-right font-mono font-bold">{fmtINR(c.ctc)}</td>
                    <td className="py-2 px-2 text-right text-xs text-zinc-500 truncate max-w-[140px]">
                      {r.note || "—"}
                    </td>
                    {isSuper && (
                      <td className="py-2 px-2 text-right whitespace-nowrap">
                        <button
                          onClick={() => onEdit(r)}
                          className="p-1 hover:bg-zinc-100 rounded"
                          data-testid={`ss-hist-edit-${r.id}`}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => onDelete(r)}
                          className="p-1 hover:bg-red-50 hover:text-red-600 rounded ml-1"
                          data-testid={`ss-hist-delete-${r.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan={isSuper ? 8 : 7} className="py-10 text-center text-zinc-500">No revisions yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}
